const OrigDateTimeFormat = Intl.DateTimeFormat;

export const BROWSER_TZ = new OrigDateTimeFormat().resolvedOptions().timeZone;
export const TIMEZONES = Intl.supportedValuesOf?.('timeZone') ?? [BROWSER_TZ];

const isValid = (tz) => {
  try {
    new OrigDateTimeFormat('en-US', { timeZone: tz });
    return !!tz;
  } catch {
    return false;
  }
};

const stored = (key) => {
  try {
    const tz = localStorage.getItem(key);
    return isValid(tz) ? tz : null;
  } catch {
    return null;
  }
};

let current = stored('timezone') || stored('business_timezone') || BROWSER_TZ;

export const getTimezone = () => current;

export const setUserTimezone = (tz) => {
  if (!isValid(tz)) return;
  localStorage.setItem('timezone', tz);
  current = tz;
};

export const clearUserTimezone = () => {
  localStorage.removeItem('timezone');
  current = stored('business_timezone') || BROWSER_TZ;
};

export const setBusinessTimezone = (tz) => {
  if (!isValid(tz)) return;
  localStorage.setItem('business_timezone', tz);
  if (!stored('timezone')) current = tz;
};

// ponytail: every toLocale*/Intl.DateTimeFormat call without an explicit timeZone
// now renders in the app timezone, so the ~40 display call sites need no changes.
const withTz = (options) =>
  options?.timeZone ? options : { ...options, timeZone: current };

function DateTimeFormat(locales, options) {
  return new OrigDateTimeFormat(locales, withTz(options));
}
DateTimeFormat.prototype = OrigDateTimeFormat.prototype;
DateTimeFormat.supportedLocalesOf = OrigDateTimeFormat.supportedLocalesOf;
Intl.DateTimeFormat = DateTimeFormat;

for (const method of ['toLocaleString', 'toLocaleDateString', 'toLocaleTimeString']) {
  const original = Date.prototype[method];
  Date.prototype[method] = function (locales, options) {
    return original.call(this, locales, withTz(options));
  };
}

const wallClock = (time, tz) => {
  const p = Object.fromEntries(
    new OrigDateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    })
      .formatToParts(new Date(time))
      .map(({ type, value }) => [type, Number(value)])
  );
  return p;
};

const offsetAt = (time, tz) => {
  const p = wallClock(time, tz);
  return (
    Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) -
    Math.floor(time / 1000) * 1000
  );
};

// Libraries that read local getters (react-big-calendar, date-fns, datetime-local
// inputs) get a Date whose local fields show the app-timezone wall clock.
// Format these with an explicit `timeZone: BROWSER_TZ`, never the patched default.
export const toZoned = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return d;
  const p = wallClock(d.getTime(), current);
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, d.getMilliseconds());
};

export const fromZoned = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return d;
  const wall = Date.UTC(
    d.getFullYear(), d.getMonth(), d.getDate(),
    d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds()
  );
  const guess = wall - offsetAt(wall, current);
  return new Date(wall - offsetAt(guess, current));
};

export const gmtOffset = (tz, date = new Date()) =>
  new OrigDateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')?.value ?? '';
